// src/shared/components/ui/modal.tsx

import { X } from 'lucide-react'
import type { HTMLAttributes, PropsWithChildren } from 'react'
import { createPortal } from 'react-dom'

import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/utils/cn'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const modalSizeMap: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-none w-[calc(100vw-2rem)] h-[calc(100vh-2rem)]',
}

interface ModalProps extends PropsWithChildren {
  open: boolean
  title: string
  description?: string
  onClose: () => void
  className?: string
  size?: ModalSize
  hideClose?: boolean
}

export function Modal({
  open,
  title,
  description,
  onClose,
  className,
  size = 'md',
  hideClose,
  children,
}: ModalProps) {
  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        aria-label="Close"
        className="absolute inset-0 bg-black/45 backdrop-blur-[6px]"
        onClick={onClose}
        type="button"
      />
      <div
        aria-modal="true"
        className={cn(
          'relative z-10 flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-xl border border-[var(--panel-border)] bg-[var(--panel)] shadow-lg',
          modalSizeMap[size],
          className,
        )}
        role="dialog"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 p-5 pb-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-[var(--color-text)]">{title}</h3>
            {description ? (
              <p className="mt-1 text-sm text-[var(--color-text-soft)]">{description}</p>
            ) : null}
          </div>
          {!hideClose ? (
            <Button aria-label="Закрыть" onClick={onClose} size="sm" type="button" variant="ghost">
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>
      </div>
    </div>,
    document.body,
  )
}

export function ModalFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 -mx-5 -mb-5 mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-[var(--panel-border)] bg-[var(--panel)] px-5 py-3',
        className,
      )}
      {...props}
    />
  )
}
